import Places from 'constants/places'
import type { FlightsClassTypes } from 'constants/flight'
import type { CharterBundleSearchTilePricing } from 'components/SearchKit/SearchTiles/accommodations/charterBundleSearchTilePricing'
import { buildBundleOfferFilters } from '../MaldivesDirectUtils'
import type { CalendarItemBundleData } from './types'

interface BuildMaldivesCheapestStreamSearchFiltersParams {
  rooms: Array<App.Occupants>
  nights: number
  fareClass: FlightsClassTypes
}

/**
 * Anytime filters that match the first tile on
 * /campaign/maldives-direct?sortBy=price.asc (stream search, limit stripped).
 */
export function buildMaldivesCheapestStreamSearchFilters({
  rooms,
  nights,
  fareClass,
}: BuildMaldivesCheapestStreamSearchFiltersParams): App.OfferListFilters | undefined {
  if (rooms.length === 0) {
    return undefined
  }

  const filters = buildBundleOfferFilters({
    destinationId: Places.Maldives.id,
    destinationName: Places.Maldives.name,
    checkIn: '',
    checkOut: '',
    rooms,
    fareClass: fareClass as App.OfferListFilters['fareClass'],
    charterNumberOfNights: nights,
  })
  const { limit: _, ...filtersWithoutLimit } = filters

  return {
    ...filtersWithoutLimit,
    sortBy: 'price.asc',
  }
}

interface MapCheapestTilePricingToBundleDataParams {
  tilePricing: CharterBundleSearchTilePricing | undefined
  guestCount: number
  useMemberPrice: boolean
  fetching: boolean
  /** True while offer ids exist but metadata for the list key is still missing. */
  waitingForMetaData?: boolean
}

const EMPTY_BUNDLE_DATA: CalendarItemBundleData = {
  fetching: false,
  available: false,
  totalPrice: 0,
  pricePerPerson: 0,
  offerCount: 0,
}

/**
 * Maps campaign-tile `lowestPrice` pricing into banner bundle data.
 * When useMemberPrice is false (Agent Hub), member fields are omitted so
 * getMaldivesBannerPriceDisplay falls through to the regular total.
 */
export function mapCheapestTilePricingToBundleData({
  tilePricing,
  guestCount,
  useMemberPrice,
  fetching,
  waitingForMetaData = false,
}: MapCheapestTilePricingToBundleDataParams): CalendarItemBundleData {
  const totalPrice = tilePricing?.price ?? 0
  const memberTotalPrice = useMemberPrice ? tilePricing?.memberPrice : undefined

  if (!totalPrice) {
    return {
      ...EMPTY_BUNDLE_DATA,
      fetching: fetching || waitingForMetaData,
    }
  }

  return {
    fetching,
    available: true,
    totalPrice,
    memberTotalPrice,
    pricePerPerson: totalPrice / guestCount,
    memberPricePerPerson: memberTotalPrice ? memberTotalPrice / guestCount : undefined,
    offerCount: 1,
  }
}
